/**
 * Block Time Slot Modal Component
 *
 * Allows vendors to block time slots for administrative reasons:
 * - Maintenance
 * - Private events
 * - Other
 *
 * Note: For walk-ins and phone bookings, use the "Add Manual Booking" feature instead.
 *
 * Time slots are dynamically generated based on the venue's operating hours.
 * Falls back to 6 AM - 11 PM if operating hours are not provided.
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState, useEffect, useMemo } from 'react';
import { X, Clock, Calendar, MapPin, FileText, AlertCircle, Phone, User, DollarSign, Hash } from 'lucide-react';
import Modal from '../ui/Modal';
import { BlockConflictError, BlockConflictDetails, OperatingHour } from '../../services/api';
import { format } from 'date-fns';

interface BlockTimeSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (blockData: BlockSlotData) => Promise<void>;
  courts: any[];
  operatingHours?: OperatingHour[];
  preFilledData?: {
    courtId?: number;
    date?: string;
    startTime?: string;
    endTime?: string;
  };
}

export interface BlockSlotData {
  court_id: number;
  date: string;
  end_date?: string; // Optional: for multi-day blocking
  start_time: string;
  end_time: string;
  reason: 'maintenance' | 'private_event' | 'other';
  notes?: string;
}

const BLOCK_REASONS = [
  { value: 'maintenance', label: 'Maintenance', icon: '🔧', description: 'Court maintenance or repairs' },
  { value: 'private_event', label: 'Private Event', icon: '🎉', description: 'Special private event' },
  { value: 'other', label: 'Other', icon: '📝', description: 'Other administrative reason' },
];

export default function BlockTimeSlotModal({
  isOpen,
  onClose,
  onSubmit,
  courts,
  operatingHours,
  preFilledData,
}: BlockTimeSlotModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflictDetails, setConflictDetails] = useState<BlockConflictDetails | null>(null);

  // Form state
  const [courtId, setCourtId] = useState<number>(preFilledData?.courtId || 0);
  const [date, setDate] = useState<string>(preFilledData?.date || '');
  const [endDate, setEndDate] = useState<string>(''); // For multi-day blocking
  const [startTime, setStartTime] = useState<string>(preFilledData?.startTime || '');
  const [endTime, setEndTime] = useState<string>(preFilledData?.endTime || '');
  const [reason, setReason] = useState<BlockSlotData['reason']>('maintenance');
  const [notes, setNotes] = useState<string>('');
  const [isAllDay, setIsAllDay] = useState<boolean>(false); // Block entire operating hours

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCourtId(preFilledData?.courtId || 0);
      setDate(preFilledData?.date || new Date().toISOString().split('T')[0]);
      setEndDate('');
      setStartTime(preFilledData?.startTime || '');
      setEndTime(preFilledData?.endTime || '');
      setReason('maintenance');
      setNotes('');
      setIsAllDay(false);
      setErrors({});
      setConflictDetails(null);
    }
  }, [isOpen, preFilledData]);

  // Generate time options based on operating hours for the selected date
  const timeOptions = useMemo(() => {
    const options: string[] = [];

    // Default hours if no operating hours or venue is closed
    let startHour = 6;
    let endHour = 23;

    // Get operating hours for the selected date
    if (date && operatingHours && operatingHours.length > 0) {
      try {
        const selectedDate = new Date(date + 'T00:00:00');
        const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();

        const daySchedule = operatingHours.find(
          (oh) => oh.day_of_week.toLowerCase() === dayOfWeek
        );

        if (daySchedule && !daySchedule.is_closed && daySchedule.opening_time && daySchedule.closing_time) {
          // Parse opening time (format: "HH:MM:SS" or "HH:MM")
          const openingParts = daySchedule.opening_time.split(':');
          const closingParts = daySchedule.closing_time.split(':');

          startHour = parseInt(openingParts[0], 10);
          endHour = parseInt(closingParts[0], 10);

          // If closing time has minutes > 0, extend end hour
          const closingMinutes = parseInt(closingParts[1], 10) || 0;
          if (closingMinutes > 0) {
            endHour = endHour + 1;
          }

          // Handle overnight hours (e.g., 15:00-00:00 or 18:00-02:00)
          if (endHour <= startHour) {
            endHour = endHour + 24;
          }
        }
      } catch (e) {
        // Silently fall back to default hours
      }
    }

    // Generate time slots from start to end hour in 30-minute intervals
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        // Don't go past closing time
        if (hour === endHour && minute > 0) break;

        const displayHour = hour % 24; // Wrap around for overnight hours
        const timeStr = `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        options.push(timeStr);
      }
    }

    return options;
  }, [date, operatingHours]);

  // Calculate number of days in the date range (for multi-day blocking)
  const dateRangeInfo = useMemo(() => {
    if (!date || !endDate) return null;

    const start = new Date(date + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    if (end < start) return null;

    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end

    return {
      days: diffDays,
      startFormatted: format(start, 'MMM d'),
      endFormatted: format(end, 'MMM d, yyyy'),
    };
  }, [date, endDate]);

  // Handle "All Day" toggle - auto-fill times based on operating hours
  const handleAllDayToggle = (checked: boolean) => {
    setIsAllDay(checked);

    if (checked && timeOptions.length > 0) {
      // Set start time to first available time
      setStartTime(timeOptions[0]);
      // Set end time to last available time
      setEndTime(timeOptions[timeOptions.length - 1]);
    }
  };

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!courtId || courtId === 0) {
      newErrors.court_id = 'Please select a court';
    }

    if (!date) {
      newErrors.date = 'Please select a date';
    }

    // Validate end date if provided (multi-day blocking)
    if (endDate && date && endDate < date) {
      newErrors.end_date = 'End date must be on or after start date';
    }

    if (!startTime) {
      newErrors.start_time = 'Please select start time';
    }

    if (!endTime) {
      newErrors.end_time = 'Please select end time';
    }

    // Use index-based comparison for overnight hours (00:00 wraps around)
    if (startTime && endTime) {
      const startIdx = timeOptions.indexOf(startTime);
      const endIdx = timeOptions.indexOf(endTime);
      if (startIdx >= 0 && endIdx >= 0 && startIdx >= endIdx) {
        newErrors.end_time = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const blockData: BlockSlotData = {
        court_id: courtId,
        date,
        end_date: endDate || undefined, // Include end_date for multi-day blocking
        start_time: startTime,
        end_time: endTime,
        reason,
        notes: notes || undefined,
      };

      await onSubmit(blockData);
      onClose();
    } catch (error: any) {
      console.error('Failed to block slot:', error);

      // Check if this is a conflict error with detailed info
      if (error instanceof BlockConflictError || error.name === 'BlockConflictError') {
        setConflictDetails(error.conflicts);
        setErrors({
          submit: error.message,
          actionRequired: error.actionRequired || 'Please cancel conflicting bookings first',
        });
      } else {
        // Display backend error message if available
        const errorMessage = error?.message || 'Failed to block time slot. Please try again.';
        setErrors({ submit: errorMessage });
        setConflictDetails(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" animate={false}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 bg-orange-100 rounded-lg">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Block Time Slot</h2>
            <p className="text-xs sm:text-sm text-gray-500">Reserve a slot</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Court Selection */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
              Court *
            </label>
            <select
              value={courtId}
              onChange={(e) => setCourtId(Number(e.target.value))}
              className={`w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm ${
                errors.court_id ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value={0}>Select a court</option>
              {Array.isArray(courts) && courts.map((court) => (
                <option key={court.id} value={court.id}>
                  {court.name} {court.sport && `- ${court.sport.name}`}
                </option>
              ))}
            </select>
            {errors.court_id && (
              <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {errors.court_id}
              </p>
            )}
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 overflow-hidden">
            <div className="min-w-0">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                Start Date *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (endDate && e.target.value > endDate) {
                    setEndDate('');
                  }
                }}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full min-w-0 max-w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm box-border ${
                  errors.date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.date && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.date}
                </p>
              )}
            </div>

            <div className="min-w-0">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                End Date <span className="text-gray-400 font-normal text-xs">(Optional)</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={date || new Date().toISOString().split('T')[0]}
                className={`w-full min-w-0 max-w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm box-border ${
                  errors.end_date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.end_date && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.end_date}
                </p>
              )}
            </div>
          </div>

          {/* Multi-day blocking summary */}
          {dateRangeInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl sm:rounded-lg p-3">
              <p className="text-xs sm:text-sm text-blue-800">
                <strong>Multi-day block:</strong> {dateRangeInfo.days} block{dateRangeInfo.days > 1 ? 's' : ''}{' '}
                from {dateRangeInfo.startFormatted} to {dateRangeInfo.endFormatted}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Each day: {startTime || '[start]'} - {endTime || '[end]'}
              </p>
            </div>
          )}

          {/* All Day Toggle */}
          <div className="flex items-center gap-2 sm:gap-3 p-3 bg-gray-50 rounded-xl sm:rounded-lg border border-gray-200">
            <input
              type="checkbox"
              id="allDay"
              checked={isAllDay}
              onChange={(e) => handleAllDayToggle(e.target.checked)}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <label htmlFor="allDay" className="flex-1 cursor-pointer">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Block entire day</span>
              <p className="text-xs text-gray-500">Fill times to cover full operating hours</p>
            </label>
          </div>

          {/* Time Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                Start Time *
              </label>
              <select
                value={startTime}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setStartTime(newStart);
                  // Use index-based comparison for overnight hours (00:00 wraps around)
                  const newStartIdx = timeOptions.indexOf(newStart);
                  const endIdx = timeOptions.indexOf(endTime);
                  if (endTime && newStartIdx >= 0 && endIdx >= 0 && newStartIdx >= endIdx) {
                    setEndTime('');
                  }
                }}
                disabled={isAllDay}
                className={`w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm ${
                  errors.start_time ? 'border-red-500' : 'border-gray-300'
                } ${isAllDay ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select time</option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              {errors.start_time && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.start_time}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                End Time *
              </label>
              <select
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={isAllDay}
                className={`w-full px-3 py-2.5 sm:py-2 border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm ${
                  errors.end_time ? 'border-red-500' : 'border-gray-300'
                } ${isAllDay ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">{startTime ? `After ${startTime}` : 'Select time'}</option>
                {timeOptions
                  .filter((time) => {
                    if (!startTime) return true;
                    // Use index-based comparison to handle midnight (00:00) wrap-around
                    const startIdx = timeOptions.indexOf(startTime);
                    const timeIdx = timeOptions.indexOf(time);
                    return timeIdx > startIdx;
                  })
                  .map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
              </select>
              {errors.end_time && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.end_time}
                </p>
              )}
            </div>
          </div>

          {/* Reason Selection */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
              Reason for Blocking *
            </label>
            <div className="grid grid-cols-1 gap-2 sm:gap-3">
              {BLOCK_REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-start p-3 sm:p-4 border-2 rounded-xl sm:rounded-lg cursor-pointer transition-all ${
                    reason === r.value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value as BlockSlotData['reason'])}
                    className="mt-0.5 text-orange-600 focus:ring-orange-500"
                  />
                  <div className="ml-2 sm:ml-3 flex-1">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <span className="text-lg sm:text-xl">{r.icon}</span>
                      <span className="font-medium text-gray-900 text-sm">{r.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{r.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
              Notes <span className="text-gray-500 font-normal text-xs">(Optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes..."
              rows={3}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-xl sm:rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
              <p className="text-sm text-red-800 flex items-center gap-2 font-medium">
                <AlertCircle className="w-5 h-5" />
                {errors.submit}
              </p>

              {/* Conflict Details */}
              {conflictDetails && conflictDetails.has_conflicts && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="bg-white rounded-lg p-3 border border-red-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">Conflict Summary:</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {conflictDetails.summary.total_bookings > 0 && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {conflictDetails.summary.total_bookings} booking(s)
                        </span>
                      )}
                      {conflictDetails.summary.total_blocks > 0 && (
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded">
                          {conflictDetails.summary.total_blocks} existing block(s)
                        </span>
                      )}
                      {conflictDetails.summary.total_revenue_at_risk > 0 && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                          {CURRENCY_SYMBOL}{Number(conflictDetails.summary.total_revenue_at_risk).toLocaleString()} at risk
                        </span>
                      )}
                    </div>
                    {conflictDetails.summary.affected_courts.length > 0 && (
                      <p className="text-xs text-gray-500 mt-2">
                        Affected courts: {conflictDetails.summary.affected_courts.join(', ')}
                      </p>
                    )}
                  </div>

                  {/* Conflicting Bookings */}
                  {conflictDetails.bookings.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Conflicting Bookings:</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {conflictDetails.bookings.map((booking, index) => (
                          <div key={index} className="bg-white rounded-lg p-3 border border-gray-200 text-sm">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-900 flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {booking.customer_name}
                              </span>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                {booking.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {booking.start_time} - {booking.end_time}
                              </div>
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {booking.court_name}
                              </div>
                              {booking.customer_phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  <a href={`tel:${booking.customer_phone}`} className="text-blue-600 hover:underline">
                                    {booking.customer_phone}
                                  </a>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {CURRENCY_SYMBOL}{Number(booking.final_price).toLocaleString()}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                              <Hash className="w-3 h-3" />
                              {booking.booking_reference}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Conflicting Blocks */}
                  {conflictDetails.blocks.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Existing Blocks:</p>
                      <div className="space-y-2">
                        {conflictDetails.blocks.map((block, index) => (
                          <div key={index} className="bg-orange-50 rounded-lg p-3 border border-orange-200 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900">
                                {block.reason === 'maintenance' ? '🔧 Maintenance' :
                                 block.reason === 'private_event' ? '🎉 Private Event' :
                                 (block.reason === 'walk_in' || block.reason === 'walk_in_booking') ? '🚶 Walk-in' :
                                 block.reason === 'phone_booking' ? '📞 Phone Booking' : '📝 Other'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {block.start_time} - {block.end_time}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {block.court_name}
                              {block.customer_name && ` • ${block.customer_name}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Required */}
                  {errors.actionRequired && (
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                      <p className="text-sm text-amber-800">
                        <strong>Action Required:</strong> {errors.actionRequired}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-gray-200 text-gray-700 rounded-xl sm:rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-orange-600 text-white rounded-xl sm:rounded-lg font-semibold hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Blocking...
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4" />
                  Block Time Slot
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
